import { useState } from 'react'
import { Card } from './Card'
import { Dialog } from './Dialog'
import { useHubLang } from '../lib/i18n'
import { ONE_QUESTION, shownIn } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import {
  ANSWER_MAX,
  answerOneQuestion,
  revealLabel,
  withdrawOneQuestionAnswer,
  type AnswerResult,
  type OneQuestionHub,
  type OneQuestionWeek,
} from '../lib/oneQuestion'

/** Names, not words — English in both languages, like Star One. */
export const ONE_QUESTION_NAME = 'One Question'
export const ONE_VOICE_NAME = 'One Voice'

/**
 * The server calls, injectable so the suite can drive the card without a
 * network. Defaults are the real ones. There is no call that reads anybody
 * else's answer: since 131 the hub has no door to them.
 */
export type OneQuestionApi = {
  answer: (contentId: string, body: string) => Promise<AnswerResult>
  withdraw: (contentId: string) => Promise<void>
}

const REAL_API: OneQuestionApi = {
  answer: answerOneQuestion,
  withdraw: withdrawOneQuestionAnswer,
}

/**
 * One Question — the weekly question, as a HOOK in the hub's rail (11 Oct).
 *
 * ── THE CARD IS THE HOOK, THE DIALOG IS THE QUESTION (David, 11 Oct) ────────
 *
 * Until 11 Oct this card sat first in the MAIN column with the whole thing on
 * it: the blurb, the box, who reads it, last week. David: "keep the main feed
 * clean … the widget is just the hook", about the countdown's size. So the
 * card shows the week, the question, the count and ONE button, and the button
 * opens <OneQuestionPanel> — everything the card used to carry, unchanged —
 * in the app's one Dialog. On a phone the card is a tile in the swipe row
 * above the feed (hub.css, `.hub__hooks`), first of the row.
 *
 * Nothing about who reads an answer moved OUT of sight of the box: the
 * sentence saying so (034, 131) is in the panel, beside the textarea, which is
 * the only place anybody types. The card itself takes no input.
 *
 * ── WHAT THE PANEL SHOWS ────────────────────────────────────────────────────
 *
 * What One Question is, in two sentences; this week's question with a box to
 * answer it, or your answer if you have given one; how many people have
 * answered (a count, never a list — rule 8); and LAST week's question with
 * its count and your own answer.
 *
 * ── NOBODY ELSE'S ANSWER, EVER (131, David 5 Oct) ───────────────────────────
 *
 * 105 put last week's named answers behind a "Read the answers" button, and
 * let a moderator read the live week. Both are gone. Communications reads the
 * answers on #/support/one-question and makes One Voice from them, quoting
 * without names — and the panel says exactly that before anybody types.
 *
 * ── EVERYTHING ABOUT TIME COMES FROM THE SERVER ─────────────────────────────
 *
 * `hub.live` is whatever the database says is live, `hub.last` whatever it
 * says has ended. Neither the card nor the panel compares a date with
 * `new Date()` — see lib/oneQuestion.ts.
 *
 * ── RE-READ, NEVER SPLICE ───────────────────────────────────────────────────
 *
 * After any write the panel calls `onChanged()` and the route re-reads
 * `one_question_hub()`. The dialog stays open across the re-read — it is
 * mounted by the card, not by the panel — so "Saved. +25 stars." is seen.
 */
export function OneQuestionCard({
  hub,
  locale,
  onChanged,
  api = REAL_API,
}: {
  /** Null while loading or when the read failed: the card renders nothing. */
  hub: OneQuestionHub | null
  locale: string
  onChanged: () => void
  api?: OneQuestionApi
}) {
  const lang = useHubLang()
  const c = ONE_QUESTION[lang]
  const [open, setOpen] = useState(false)

  // Absent, not empty: before 12 Oct and after the last week there is nothing
  // to ask, and a card saying so would be a card about the schedule.
  if (!hub || (!hub.live && !hub.last)) return null

  // The hook shows the live week; with none live (the week after the last),
  // last week's question, labelled as such.
  const week = hub.live ?? hub.last!
  const answered = !!hub.live && hub.live.myAnswer !== null && !hub.live.myAnswerRemoved
  const label = !hub.live
    ? c.seeLastWeek
    : answered || hub.live.myAnswerRemoved
      ? c.seeYourAnswer
      : c.answerFor(hub.live.starsForAnswer)

  return (
    <Card className="oneq hook" title={ONE_QUESTION_NAME} action={<span className="hook__eyebrow">{c.weekOf(week.seq)}</span>}>
      <p className="hook__question">{week.body}</p>
      <p className="hook__meta">{hub.live ? c.answeredShort(hub.live.answerCount) : c.answers(week.answerCount)}</p>
      <button
        type="button"
        className={`btn btn--sm btn--block ${hub.live && !answered && !hub.live.myAnswerRemoved ? 'btn--primary' : 'btn--quiet'}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {label}
      </button>
      <Dialog open={open} title={ONE_QUESTION_NAME} onClose={() => setOpen(false)}>
        <OneQuestionPanel hub={hub} locale={locale} onChanged={onChanged} api={api} />
      </Dialog>
    </Card>
  )
}

/**
 * Everything One Question says and does — the card's contents until 11 Oct,
 * now the dialog the hub card opens. Renders nothing for an empty hub.
 */
export function OneQuestionPanel({
  hub,
  locale,
  onChanged,
  api = REAL_API,
}: {
  hub: OneQuestionHub | null
  locale: string
  onChanged: () => void
  api?: OneQuestionApi
}) {
  const lang = useHubLang()
  const c = ONE_QUESTION[lang]
  if (!hub || (!hub.live && !hub.last)) return null
  return (
    <div className="oneq__panel">
      {hub.live && hub.live.starsForAnswer > 0 && !hub.live.myAnswer && (
        <p><span className="pill">{c.answerFor(hub.live.starsForAnswer)}</span></p>
      )}
      <p className="oneq__about">{c.about}</p>
      {hub.live && <LiveWeek week={hub.live} locale={locale} onChanged={onChanged} api={api} />}
      {hub.last && <LastWeek week={hub.last} />}
    </div>
  )
}

function QuestionText({ week }: { week: OneQuestionWeek }) {
  const lang = useHubLang()
  const c = ONE_QUESTION[lang]
  return (
    <>
      <p className="oneq__eyebrow">{c.weekOf(week.seq)}</p>
      <p className="oneq__question">{week.body}</p>
      {week.isFallback && week.variantLocale && (
        <p className="oneq__fallback">{shownIn(lang, week.variantLocale)}</p>
      )}
    </>
  )
}

function LiveWeek({
  week,
  locale,
  onChanged,
  api,
}: {
  week: OneQuestionWeek
  locale: string
  onChanged: () => void
  api: OneQuestionApi
}) {
  const lang = useHubLang()
  const c = ONE_QUESTION[lang]
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const closes = revealLabel(week.closesAt, locale)
  const hasAnswer = week.myAnswer !== null && !week.myAnswerRemoved
  const showForm = !week.myAnswerRemoved && (!hasAnswer || editing)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.answer(week.contentId, body)
      // Only a FIRST answer pays, and only up to the cap — so the line says
      // what the server actually paid, never the advertised rate.
      setStatus(r.isNew && r.starsAwarded > 0 ? c.earned(r.starsAwarded) : c.saved)
      setEditing(false)
      setDraft('')
      onChanged()
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  async function withdraw() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.withdraw(week.contentId)
      setStatus(null)
      onChanged()
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="oneq__week">
      <QuestionText week={week} />

      {week.myAnswerRemoved && <p className="oneq__note">{c.removedByMod}</p>}

      {hasAnswer && !editing && (
        <div className="oneq__mine">
          <p className="oneq__label">{c.yourAnswer}</p>
          <blockquote className="oneq__answer">{week.myAnswer}</blockquote>
          <div className="oneq__actions">
            <button
              type="button"
              className="btn btn--quiet btn--xs"
              onClick={() => {
                setDraft(week.myAnswer ?? '')
                setEditing(true)
                setStatus(null)
              }}
              disabled={busy}
            >
              {c.edit}
            </button>
            <button type="button" className="btn btn--quiet btn--xs" onClick={withdraw} disabled={busy}>
              {c.withdraw}
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <form className="oneq__form" onSubmit={submit}>
          <textarea
            className="composer__body oneq__input"
            value={draft}
            maxLength={ANSWER_MAX}
            rows={2}
            placeholder={c.placeholder}
            aria-label={week.body ?? ONE_QUESTION_NAME}
            onChange={(e) => setDraft(e.target.value)}
          />
          {/* Said BEFORE anybody types (034, 131): Communications alone
              reads it, with the name, and One Voice may quote it unnamed. */}
          <p className="oneq__note">{c.named(closes)}</p>
          <div className="oneq__actions">
            <span className="oneq__count" aria-live="polite">
              {draft.length}/{ANSWER_MAX}
            </span>
            {editing && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => setEditing(false)} disabled={busy}>
                {c.cancel}
              </button>
            )}
            <button className="btn btn--primary btn--sm" disabled={busy || !draft.trim()}>
              {editing ? c.save : c.submit}
            </button>
          </div>
        </form>
      )}

      {status && (
        <p className="oneq__status" role="status">
          {status}
        </p>
      )}
      {error && (
        <p className="oneq__error" role="alert">
          {c.failed} {error}
        </p>
      )}

      <p className="oneq__count-line">{c.answeredSoFar(week.answerCount)}</p>
    </div>
  )
}

function LastWeek({ week }: { week: OneQuestionWeek }) {
  const lang = useHubLang()
  const c = ONE_QUESTION[lang]
  return (
    <div className="oneq__week oneq__week--last">
      <p className="oneq__label">{c.lastWeek}</p>
      <QuestionText week={week} />
      {week.myAnswer && !week.myAnswerRemoved && (
        <p className="oneq__note">
          {c.yourAnswer}: “{week.myAnswer}”
        </p>
      )}
      <p className="oneq__note">{c.answers(week.answerCount)}</p>
    </div>
  )
}
